import { parsePhoneNumber } from 'libphonenumber-js'
import { ChevronRight, ExternalLink, X } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useState } from 'react'

import { Button, ButtonGroup, ButtonLink, IconButton } from 'slab'
import { MemberOnboardingAllowanceQmeTreatment } from 'utils/web_api.schemas'

import { useOnboarding } from './member_onboarding_v2/OnboardingContext'
import { type OnboardingInfo } from './member_onboarding_v2/types'

const RoyalAmericanPhoneNumber = '8774950587'
const getPhoneNumber = (onboardingInfo?: OnboardingInfo | null): string | null => {
  return (
    onboardingInfo?.employer?.is_ichra_shop_employer
      ? parsePhoneNumber(RoyalAmericanPhoneNumber, 'US').formatNational()
      : null
  )
}

const getPlanSelectionVideoLink = (onboardingInfo?: OnboardingInfo | null) : string => {
  // https://linear.app/thatch/issue/ME-164/bring-back-help-and-support-during-oep-onboarding
  const QME_SPEND_ALLOWED_LINK = 'https://www.youtube.com/watch?v=nEDYeo_FZZI'
  const QME_SPEND_NOT_ALLOWED_LINK = 'https://www.youtube.com/watch?v=hbHBy4SV7P8'

  // Note: If we don't have QME treatment info, fall back to the not-allowed link
  const qmeAllowed = onboardingInfo?.allowance?.qme_treatment === MemberOnboardingAllowanceQmeTreatment.all_allowed
  return qmeAllowed ? QME_SPEND_ALLOWED_LINK : QME_SPEND_NOT_ALLOWED_LINK
}

const MotionButton = motion.create(Button)
const MotionButtonLink = motion.create(ButtonLink)

interface IProps {
  onDismiss: () => void;
}

const MemberOnboardingSupportPopoverContent = ({ onDismiss } : IProps) => {
  const { onboardingData } = useOnboarding()
  const [selectedOption, setSelectedOption] = useState<number | null>(null)

  const planSelectionVideo = getPlanSelectionVideoLink(onboardingData)

  type ListOption = {
    title: string
    content: ReactNode
    cta_text?: string | null
    cta_link?: string | null
  }
  const optionsList: ListOption[] = [
    {
      title: "I'm not sure how to select a plan",
      content:
        "We've put together a short video that explains the process of selecting a health insurance plan and some things you might want to keep in mind when picking your plan. Click the link below to watch it.",
      cta_text: 'Watch video',
      cta_link: planSelectionVideo,
    },
    {
      title: 'I need help with something else',
      content:
        "We're here to help! Just look for the purple help icon on the lower right-hand corner of your screen, and ask your questions in the chat.",
      cta_text: null,
      cta_link: null,
    },
  ]

  const bookACallLink = onboardingData?.member?.plan_selection_call_scheduling_links?.en
  if (bookACallLink) {
    optionsList.splice(1, 0, {
      title: 'I need help deciding between plans',
      content: (
        <>
          Have questions about which plan is right for you? Start by chatting with us — just select the purple help icon in the lower right corner.<br />
          <br />
          If you'd like more hands-on guidance, you can also book a call with a licensed advisor.
        </>
      ),
      cta_text: 'Book a call',
      cta_link: bookACallLink,
    })
  }

  const firstName = onboardingData?.member?.given_name
  const phoneNumber = getPhoneNumber(onboardingData)
  const displayCallButton = selectedOption === 1 && phoneNumber

  return (
    <div className='h-full flex flex-col'>
      <div className='mb-4'>
        <div className='flex justify-between'>
          <img
            src='https://thatch.com/logo.svg'
            alt='Thatch logo'
            className='w-8 h-8'
          />
          <div className='flex h-4 w-4 items-center justify-center'>
            <IconButton icon={X} onClick={() => onDismiss()} label='Dismiss' />
          </div>
        </div>
      </div>
      {selectedOption === null && (
        <div className='flex flex-col justify-between h-full'>
          <div className='flex flex-col space-y-1 mb-4'>
            <div className='body-bold text-gray-500'>
              Hello{firstName && ` ${firstName}`}!
            </div>
            <div className='heading-md text-gray-800'>
              What can we help with today?
            </div>
          </div>
          <AnimatePresence>
            <motion.div
              className='flex flex-col py-1.5 space-y-1 border rounded-lg overflow-clip'
              key='buttons'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {optionsList.map((option, index) => (
                <button
                  className='py-0.5 px-2 group/menuitem outline-none text-left'
                  key={option.title}
                  onClick={() => setSelectedOption(index)}
                  type='button'
                >
                  <div className='p-2 rounded-md bg-transparent flex gap-4 items-center justify-between body transition cursor-pointer text-gray-800 group-hover/menuitem:bg-gray-50 outline outline-2 outline-offset-2 outline-transparent group-focus-visible/menuitem:outline-thatch-500'>
                    <div>{option.title}</div>
                    <div className='transition text-gray-500 group-hover/menuitem:text-gray-600'>
                      <ChevronRight size={16} />
                    </div>
                  </div>
                </button>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {selectedOption !== null && (
        <AnimatePresence>
          <motion.div
            className='h-full flex flex-col justify-between gap-6'
            key='content'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className='flex flex-col gap-4'>
              <h2 className='heading-sm text-gray-800'>
                {optionsList[selectedOption].title}
              </h2>
              <div>
                <motion.p
                  className='body'
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {optionsList[selectedOption].content}
                </motion.p>
              </div>
            </div>
            <div>
              <ButtonGroup layout='vertical'>
                {displayCallButton && (
                  <MotionButtonLink
                    variant='primary'
                    width='full'
                    href={`tel:${phoneNumber}`}
                  >
                    Call {phoneNumber}
                  </MotionButtonLink>
                )}
                {optionsList[selectedOption].cta_link && optionsList[selectedOption].cta_text && (
                  <MotionButton
                    rightIcon={ExternalLink}
                    variant={displayCallButton ? 'secondary' : 'primary'}
                    onClick={() =>
                      window.open(optionsList[selectedOption].cta_link!, '_blank')}
                    width='full'
                  >
                    {optionsList[selectedOption].cta_text}
                  </MotionButton>
                )}
                <MotionButton
                  variant='tertiary-deemphasized'
                  onClick={() => setSelectedOption(null)}
                  width='full'
                >
                  Back
                </MotionButton>
              </ButtonGroup>
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    </div>
  )
}

export default MemberOnboardingSupportPopoverContent
